import { useState } from 'react'
import { PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js'
import toast from 'react-hot-toast'
import { Button } from 'components/Button'
import { CreditCard, Zap, Lock, ShieldCheck } from 'lucide-react'
import SavedPaymentMethods from "components/SavedPaymentMethods";
import Loader from 'components/StripeElement/Loader'

const CheckoutForm = ({ onSuccess, clientSecret }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isExpressLoading, setIsExpressLoading] = useState(true)
  const [isFormValid, setIsFormValid] = useState(false)

  const handlePayment = async (e?: any, targetPaymentMethodId = null) => {
    if (e?.preventDefault) e.preventDefault()
    if (!stripe) return

    setIsProcessing(true)

    let error: any
    let paymentIntent: any

    if (targetPaymentMethodId) {
      const res = await stripe.confirmCardPayment(clientSecret, {
        payment_method: targetPaymentMethodId,
      })
      error = res.error
      paymentIntent = res.paymentIntent
    } else {
      if (!elements) {
        setIsProcessing(false)
        return
      }
      const res = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })
      error = res.error
      paymentIntent = res.paymentIntent
    }

    setIsProcessing(false)

    if (error) {
      e?.paymentFailed?.({ message: error.message })
      toast.error(error.message || 'Payment failed. Please try again.')
      return
    }

    if (paymentIntent?.status === 'succeeded') {
      toast.success('Payment successful!')
      onSuccess()
    }
  }

  return (
    <div className="bg-white border border-secondary-200/90 rounded-2xl p-5 sm:p-7 shadow-xs">
      <Loader open={isProcessing} />
      <div className={`flex flex-col lg:flex-row gap-4 lg:gap-5 items-start`}>
        <div className={'w-full lg:flex-1 space-y-4'}>
          <div className="flex items-center gap-2.5 border-b border-secondary-100 pb-3">
            <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600 border border-amber-100">
              <Zap size={17} />
            </div>
            <div>
              <h3 className="font-semibold text-secondary-900 text-sm leading-none">Quick Pay</h3>
              <p className="text-[11px] text-secondary-400 mt-0.5">Fast, easy, and secure way to pay</p>
            </div>
          </div>

          <div className="bg-secondary-50/70 border border-secondary-100 rounded-xl p-3.5 space-y-3 [&_iframe]:mb-3 [&_iframe:last-child]:mb-0 [&_.StripeElement]:space-y-3">
            {isExpressLoading && (
              <div className="space-y-2.5 py-0.5">
                <div className="skeleton h-11 w-full rounded-xl"></div>
                <div className="skeleton h-11 w-full rounded-xl"></div>
                <div className="skeleton h-11 w-full rounded-xl"></div>
              </div>
            )}
            <div className={isExpressLoading ? 'hidden' : 'block space-y-3'}>
              <ExpressCheckoutElement
                onConfirm={handlePayment}
                onReady={() => setIsExpressLoading(false)}
                onLoadError={() => setIsExpressLoading(false)}
                options={{
                  layout: { maxColumns: 1, maxRows: 4 },
                  paymentMethods: { applePay: 'always', googlePay: 'always' },
                  wallets: { applePay: 'always', googlePay: 'always' },
                }}
              />

              <SavedPaymentMethods
                onClick={(res: any) => handlePayment(null, res)}
              />
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-secondary-400 pt-1 font-medium">
            <ShieldCheck size={14} className="text-emerald-500 shrink-0" />
            <span>Guaranteed Safe & Secure Payment</span>
          </div>
        </div>

        <div className="w-full lg:w-auto flex lg:flex-col items-center justify-center py-2 lg:py-0 self-stretch shrink-0">
          <div className="flex-grow border-t lg:border-t-0 lg:border-l border-secondary-200 w-full lg:w-auto" />
          <span className="shrink mx-3 lg:mx-0 lg:my-2 text-[10px] font-bold tracking-wider text-secondary-400 bg-secondary-50 px-2 py-0.5 rounded-full border border-secondary-200/80">
            OR
          </span>
          <div className="flex-grow border-t lg:border-t-0 lg:border-l border-secondary-200 w-full lg:w-auto" />
        </div>

        <div className="w-full lg:flex-1 space-y-4">
          <div className="flex items-center gap-2.5 border-b border-secondary-100 pb-3">
            <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-100">
              <CreditCard size={17} />
            </div>
            <div>
              <h3 className="font-semibold text-secondary-900 text-sm leading-none">Credit / Debit Card</h3>
              <p className="text-[11px] text-secondary-400 mt-0.5">Pay securely using your card details</p>
            </div>
          </div>

          {/* Not a <form>: this renders inside the insurance step's Formik form,
              and nested forms are illegal HTML — the browser drops the inner one,
              so a type="submit" button here submits the OUTER form and the card is
              never confirmed. Plain div + onClick works wherever it's embedded. */}
          <div className="space-y-4">
            <div className="bg-secondary-50/70 border border-secondary-100 rounded-xl p-3.5">
              <PaymentElement
                options={{ wallets: { link: 'never' }, layout: 'tabs' }}
                onChange={(e) => setIsFormValid(e.complete)}
              />
            </div>

            <div className="flex justify-center pt-2">
              <Button
                type="button"
                onClick={handlePayment}
                title="Submit Payment"
                variant="tertiary"
                icon={<Lock size={15} />}
                disabled={!stripe || isProcessing || !isFormValid}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CheckoutForm
